import clsx from "clsx";
import { forwardRef, type KeyboardEvent } from "react";
import type { CaseListItem } from "../../../../shared/contracts.js";
import { StatusBadge } from "../../../ui/StatusBadge.js";
import {
  formatShortInstant,
  progressLabel,
  progressPercent,
  resolveStatusTone,
  roLabel,
} from "../presentation.js";

/**
 * One row of the centre list (spec §10 "Center list"): vehicle and customer,
 * RO or Pre-RO with the case number, the assigned advisor and when the order
 * last changed, the status as icon + label + colour, verified-versus-ordered
 * progress, and the unread marker — every value as the server returned it.
 *
 * The row is a button: selecting it opens the case in the inspector (the
 * parent writes `case=<id>` into the URL) without leaving the list. The
 * selected row carries `aria-current="true"`; ArrowUp/ArrowDown move between
 * rows so a keyboard user can scan the list without tabbing through every
 * control.
 */

export interface CaseListRowProps {
  item: CaseListItem;
  selected: boolean;
  onSelect: (caseId: string) => void;
  /** Focus the previous/next row on ArrowUp/ArrowDown. */
  onMoveFocus?: (direction: -1 | 1) => void;
}

export const CaseListRow = forwardRef<HTMLButtonElement, CaseListRowProps>(
  function CaseListRow({ item, selected, onSelect, onMoveFocus }, ref) {
    const tone = resolveStatusTone(item.status, item.needsAttention);

    const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
      if (!onMoveFocus) return;
      if (event.key === "ArrowDown") {
        event.preventDefault();
        onMoveFocus(1);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        onMoveFocus(-1);
      }
    };

    return (
      <li className="case-list__item">
        <button
          ref={ref}
          type="button"
          className={clsx("case-row", selected && "case-row--selected")}
          aria-current={selected ? "true" : undefined}
          data-case-id={item.id}
          data-status={item.status}
          data-tone={tone}
          data-unread={item.unread ? "true" : undefined}
          onClick={() => onSelect(item.id)}
          onKeyDown={onKeyDown}
        >
          <span className="case-row__marker" aria-hidden="true" />
          <span className="case-row__body">
            <span className="case-row__headline">
              <span className="case-row__vehicle">{item.vehicleLabel}</span>
              {/* Words, not a colour: this app never lets colour carry meaning alone. */}
              {item.carInShop ? <span className="case-row__in-shop">Car in shop</span> : null}
              {item.unread ? (
                <span className="case-row__unread">
                  <span className="visually-hidden">Unread</span>
                </span>
              ) : null}
            </span>
            <span className="case-row__customer">{item.customerName}</span>
            <span className="case-row__identity">
              <span
                className={clsx(
                  "case-row__ro",
                  item.roNumber === null && "case-row__ro--pre",
                )}
              >
                {roLabel(item.roNumber)}
              </span>
              <span className="case-row__dot" aria-hidden="true">
                ·
              </span>
              <span className="case-row__number">{item.caseNumber}</span>
              <span className="case-row__dot" aria-hidden="true">
                ·
              </span>
              <span className="case-row__vin">
                VIN {item.vinEnding === null ? "—" : `…${item.vinEnding}`}
              </span>
            </span>
            <span className="case-row__meta">
              <span className="case-row__advisor">
                <span className="case-row__meta-label">Advisor</span>{" "}
                {item.advisorName}
              </span>
              <span className="case-row__updated">
                <span className="case-row__meta-label">Updated</span>{" "}
                <time dateTime={item.updatedAt}>
                  {formatShortInstant(item.updatedAt)}
                </time>
              </span>
            </span>
          </span>
          <span className="case-row__aside">
            <StatusBadge
              status={item.status}
              needsAttention={item.needsAttention}
            />
            <span className="case-row__progress">
              <span className="case-row__progress-label">
                {progressLabel(item.quantityVerified, item.quantityOrdered)}
              </span>
              <span className="case-row__bar" aria-hidden="true">
                <span
                  className="case-row__bar-fill"
                  style={{
                    width: `${progressPercent(item.quantityVerified, item.quantityOrdered)}%`,
                  }}
                />
              </span>
            </span>
          </span>
        </button>
      </li>
    );
  },
);
